import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryIcon from '@mui/icons-material/History';
import {
  formatHistoryDate, formatHistoryValue, getEventPresentation,
  getHistoryChanges, getHistorySummary,
} from '../utils/equipmentHistory';

const sourceLabels = { web: 'Web app', android: 'Android app', sync: 'Sync', system: 'System' };
const resultColor = (value) => {
  if (value === 'Pass') return 'success';
  if (value === 'Fail') return 'error';
  if (['NA', 'N/A', 'Not applicable'].includes(value)) return 'default';
  return null;
};
const gridColumns = { xs: '1fr 1fr', sm: 'minmax(180px, 1.4fr) minmax(0, 1fr) minmax(0, 1fr)' };

function Value({ value, field, photoLabel }) {
  const formatted = photoLabel || formatHistoryValue(value, field);
  const color = ['inspection_result', 'checklist_answer'].includes(field) ? resultColor(formatted) : null;
  return color ? (
    <Chip size="small" label={formatted === 'NA' ? 'N/A' : formatted} color={color} variant="outlined" sx={{ fontWeight: 700 }} />
  ) : (
    <Typography component="span" variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: formatted === 'Not set' ? 'text.secondary' : 'text.primary' }}>
      {formatted}
    </Typography>
  );
}

function ChangeTable({ changes, label }) {
  if (!changes.length) return null;
  return (
    <Box role="table" aria-label={label} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
      <Box role="row" sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: gridColumns, bgcolor: 'background.default', px: 2, py: 1, gap: 2 }}>
        {['Field / question', 'Before', 'After'].map(heading => <Typography key={heading} role="columnheader" variant="caption" fontWeight={700} color="text.secondary">{heading}</Typography>)}
      </Box>
      {changes.map((change) => {
        const photo = change.field === 'image_uuid';
        return (
          <Box key={change.key} role="row" sx={{ display: 'grid', gridTemplateColumns: gridColumns, gap: { xs: 1, sm: 2 }, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
            <Box role="rowheader" sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{change.label}</Typography>
              {change.note && <Typography variant="caption" color="text.secondary">{change.note}</Typography>}
            </Box>
            {['from', 'to'].map(side => (
              <Box key={side} role="cell" sx={{ minWidth: 0, borderRadius: 1, p: 0.75, mt: { xs: 0, sm: -0.5 }, bgcolor: side === 'to' && change.changed !== false ? 'action.selected' : 'background.default' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' }, mb: 0.5 }}>{side === 'from' ? 'Before' : 'After'}</Typography>
                <Value value={change[side]} field={change.field} photoLabel={photo ? (!change[side] ? 'No photo' : side === 'from' ? 'Previous photo' : change.from ? 'Replacement photo' : 'Photo added') : null} />
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

function ChecklistChanges({ checklist }) {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? checklist.rows : checklist.rows.filter(row => row.changed);
  const counts = checklist.afterCounts;
  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} sx={{ mb: 1.25 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>Inspection checklist</Typography>
          <Typography variant="caption" color="text.secondary">
            {checklist.changedCount} {checklist.changedCount === 1 ? 'answer changed' : 'answers changed'} · {counts.total} answers after this update
          </Typography>
        </Box>
        <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
          <Chip size="small" variant="outlined" color="success" label={`${counts.pass} Pass`} />
          <Chip size="small" variant="outlined" color="error" label={`${counts.fail} Fail`} />
          <Chip size="small" variant="outlined" label={`${counts.na} N/A`} />
        </Stack>
      </Stack>
      <ChangeTable label="Checklist answer changes" changes={rows.map(row => ({
        ...row, label: row.question, field: 'checklist_answer',
        note: row.kind === 'added' ? 'Answer recorded' : row.kind === 'removed' ? 'Answer removed' : !row.changed ? 'Unchanged' : null,
      }))} />
      {!rows.length && <Typography variant="body2" color="text.secondary">No checklist answers changed.</Typography>}
      {checklist.rows.length > checklist.changedCount && (
        <Button size="small" onClick={() => setShowAll(!showAll)} aria-expanded={showAll} sx={{ mt: 1 }}>
          {showAll ? 'Show changed answers only' : `Show all ${checklist.rows.length} checklist answers`}
        </Button>
      )}
    </Box>
  );
}

function ResultTransition({ changes }) {
  const result = changes.fields.find(change => change.field === 'inspection_result');
  if (!result) return null;
  return (
    <Stack component="span" role="group" direction="row" gap={0.75} alignItems="center" aria-label={`Inspection result: before ${formatHistoryValue(result.from, 'inspection_result')}, after ${formatHistoryValue(result.to, 'inspection_result')}`}>
      <Value value={result.from} field="inspection_result" />
      <ArrowForwardIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
      <Value value={result.to} field="inspection_result" />
    </Stack>
  );
}

function HistoryEvent({ event, initiallyExpanded }) {
  const { row, changes, presentation } = event;
  const photoDetails = changes.fields.filter(change => change.field === 'image_uuid').map(change => ({ ...change, field: 'photo_reference', label: 'Photo reference' }));
  const details = [...changes.details, ...photoDetails];
  return (
    <Accordion defaultExpanded={initiallyExpanded} disableGutters slotProps={{ transition: { unmountOnExit: true } }} sx={{
      border: 1, borderColor: 'divider', borderRadius: '12px !important', boxShadow: 'none',
      '&:before': { display: 'none' }, overflow: 'hidden',
    }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`history-content-${event.key}`} id={`history-summary-${event.key}`} sx={{ px: { xs: 1.5, sm: 2.5 }, '& .MuiAccordionSummary-content': { my: 1.25, minWidth: 0 } }}>
        <Box sx={{ width: '100%', minWidth: 0, pr: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} alignItems={{ sm: 'center' }}>
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              <Chip component="span" label={presentation.label} size="small" color={presentation.color} variant="outlined" sx={{ fontWeight: 700 }} />
              <ResultTransition changes={changes} />
            </Stack>
            <Typography component="span" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{formatHistoryDate(row.performed_at)}</Typography>
          </Stack>
          <Typography component="div" variant="body2" sx={{ mt: 1, fontWeight: 600 }}>{getHistorySummary(row, changes)}</Typography>
          <Typography component="div" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {row.performed_by || 'User not recorded'}{row.source ? ` · ${sourceLabels[row.source] || formatHistoryValue(row.source)}` : ''}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 2.5, pt: 0 }}>
        {row.note && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, overflowWrap: 'anywhere' }}>{formatHistoryValue(row.note)}</Typography>}
        <ChangeTable changes={changes.fields} label="Equipment and inspection changes" />
        {changes.checklist && <ChecklistChanges checklist={changes.checklist} />}
        {!changes.fields.length && !changes.checklist && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{details.length ? 'This event contains record details only.' : 'No field changes were recorded for this event.'}</Typography>}
        {(details.length > 0 || row.client_version) && (
          <Box component="details" sx={{ mt: 2, '& > summary': { cursor: 'pointer', color: 'text.secondary', fontSize: 13, fontWeight: 600, py: 0.75, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } } }}>
            <Box component="summary">Record details{details.length ? ` (${details.length})` : ''}</Box>
            <Box sx={{ mt: 1 }}><ChangeTable changes={details} label="Additional record details" /></Box>
            {row.client_version && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>App version: {formatHistoryValue(row.client_version)}</Typography>}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default function ItemHistoryDialog({ open, onClose, item, rows = [], loading = false, error = '', onRetry }) {
  const [filter, setFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(30);
  useEffect(() => { setFilter('all'); setVisibleCount(30); }, [open, item?.id]);
  const events = useMemo(() => rows.map((row, index) => ({
    key: row.id || String(index), row, changes: getHistoryChanges(row), presentation: getEventPresentation(row),
  })).sort((a, b) => (Date.parse(b.row.performed_at) || 0) - (Date.parse(a.row.performed_at) || 0)), [rows]);
  const filtered = events.filter(event => filter === 'all' || event.row.event_type === filter);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" aria-labelledby="item-history-title" PaperProps={{ sx: { height: '90dvh', maxHeight: '94dvh', m: { xs: 1, sm: 3 }, width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 48px)' } } }}>
      <DialogTitle id="item-history-title" sx={{ pb: 1.5 }}>
        <Stack direction="row" gap={1.25} alignItems="center">
          <HistoryIcon color="primary" />
          <Typography component="span" variant="h6">Item history</Typography>
        </Stack>
        <Typography component="div" variant="body2" sx={{ mt: 0.75, fontWeight: 600, overflowWrap: 'anywhere' }}>
          {[item?.equipment_tag, item?.description].filter(Boolean).join(' · ') || 'Equipment activity'}
        </Typography>
        {item?.area && <Typography component="div" variant="caption" color="text.secondary">{item.area}</Typography>}
      </DialogTitle>
      <Box sx={{ px: 3, pb: 1.75, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Typography variant="caption" color="text.secondary">{loading ? 'Loading activity…' : `${events.length} ${events.length === 1 ? 'event' : 'events'} · Newest first`}<br />Times shown in {timeZone}</Typography>
        <TextField select label="Event type" size="small" value={filter} onChange={event => { setFilter(event.target.value); setVisibleCount(30); }} sx={{ minWidth: 180 }}>
          <MenuItem value="all">All events</MenuItem>
          <MenuItem value="inspected">Inspections</MenuItem>
          <MenuItem value="edited">Edits</MenuItem>
          <MenuItem value="created">Created</MenuItem>
          <MenuItem value="deleted">Deleted</MenuItem>
          <MenuItem value="restored">Restored</MenuItem>
        </TextField>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'background.default', px: { xs: 1.5, sm: 3 }, py: 2.5 }}>
        {loading ? <Box role="status"><LinearProgress /><Typography variant="body2" sx={{ mt: 2 }}>Loading item history…</Typography></Box>
          : error ? <Alert severity="error" action={onRetry && <Button color="inherit" onClick={onRetry}>Retry</Button>}>{error}</Alert>
            : !filtered.length ? <Box sx={{ textAlign: 'center', py: 6 }}><HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} /><Typography color="text.secondary">{events.length ? 'No events match this filter.' : 'No history has been recorded for this item.'}</Typography></Box>
              : <Stack gap={1.5}>
                {filtered.slice(0, visibleCount).map((event, index) => <HistoryEvent key={`${item?.id}-${filter}-${event.key}`} event={event} initiallyExpanded={index === 0} />)}
                {filtered.length > visibleCount && <Button onClick={() => setVisibleCount(count => count + 30)}>Show more events ({filtered.length - visibleCount} remaining)</Button>}
              </Stack>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}><Button variant="contained" onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
